import { createContext, useEffect, useMemo, useState } from 'react'

import moment from 'moment'
import { useSelector } from 'react-redux'
import { selectUser } from '../reducers/UserReducer'
import { getInternshipStudentEvaluations } from '../utils/api/evaluationInternship'
import { getEvaluationQuestions } from '../utils/api/evaluation'
import { getStudentInternships } from '../utils/api/student'
import { OtherRoles } from '../utils/constants'
import { getStudentEvaluationStartDate, isStudentEvaluationAvailable } from '../utils/entities/internship'

export const EvaluationsContext = createContext()

const MAX_TIMEOUT_DELAY = 2147483647

const EvaluationsProvider = ({ children }) => {
  const user = useSelector(selectUser)

  const [questions, setQuestions] = useState({})
  const [evaluations, setEvaluations] = useState({})
  const [internshipsToBeEvaluated, setInternshipsToBeEvaluated] = useState([])
  const [now, setNow] = useState(() => moment())

  useEffect(() => {
    if (user.id && user.roles.includes(OtherRoles.STUDENT)) {
      getStudentInternships(user, user.student, { studentEvaluated: false, contexts: ['sector', 'institution'] }).then(json => {
        if (json?.data) {
          setInternshipsToBeEvaluated(json.data)
        }
      })
    }
  }, [user.id])

  const internshipsToEvaluate = useMemo(
    () => internshipsToBeEvaluated.filter(internship => isStudentEvaluationAvailable(internship, now)),
    [internshipsToBeEvaluated, now]
  )

  useEffect(() => {
    const nextStartDate = internshipsToBeEvaluated
      .map(getStudentEvaluationStartDate)
      .filter(startDate => startDate !== null && startDate.isAfter(now))
      .sort((a, b) => a.diff(b))[0]

    if (nextStartDate === undefined) {
      return
    }

    const timeout = setTimeout(() => setNow(moment()), Math.min(nextStartDate.diff(now), MAX_TIMEOUT_DELAY))

    return () => clearTimeout(timeout)
  }, [internshipsToBeEvaluated, now])

  const removeInternshipToEvaluate = internship => {
    setInternshipsToBeEvaluated(internships => internships.filter(item => item.id !== internship.id))
  }

  const getQuestions = institution => {
    if (questions[institution.id]) {
      return new Promise(resolve => {
        resolve(questions[institution.id])
      })
    }

    return getEvaluationQuestions(user, institution)
      .then(json => {
        const data = json?.data ?? []
        const newQuestions = { ...questions }

        newQuestions[institution.id] = data

        setQuestions(newQuestions)

        return data
      })
  }

  const getEvaluations = internship => {
    if (evaluations[internship.id]) {
      return new Promise(resolve => {
        resolve(evaluations[internship.id])
      })
    }

    return getInternshipStudentEvaluations(user, internship).then(json => {
      const data = json?.data ?? []
      const newEvaluations = { ...evaluations }

      newEvaluations[internship.id] = data

      setEvaluations(newEvaluations)

      return data
    })
  }

  const deleteEvaluations = (internship) => {
    const newEvaluations = { ...evaluations }

    delete newEvaluations[internship.id]

    setEvaluations(newEvaluations)
  }

  return (
    <EvaluationsContext.Provider
      value={{ getQuestions, getEvaluations, deleteEvaluations, internshipsToEvaluate, removeInternshipToEvaluate }}
    >
      {children}
    </EvaluationsContext.Provider>
  )
}

export default EvaluationsProvider
